import cn from 'clsx'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'
import 'video.js/dist/video-js.css'

interface IProps {
	src: string
	type: string
	isLoading: boolean
	onPlayerReady?: (player: Player) => void
	source?: string
}

const VideoPlayer: FC<IProps> = ({
	src,
	type,
	isLoading,
	onPlayerReady,
	source
}) => {
	const [ready, setReady] = useState(false)
	const videoRef = useRef<HTMLVideoElement>(null)
	const playerRef = useRef<Player | null>(null)

	// Temporary soluton!
	useEffect(() => {
		const timer = setTimeout(() => setReady(true), 500)
		return () => clearTimeout(timer)
	}, [])

	const isSafari = useMemo(() => {
		return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
	}, [])

	const correctType = useMemo(() => {
		if (type?.includes('mp4')) return 'video/mp4'
		if (type?.includes('mpegurl') || src?.endsWith('.m3u8'))
			return 'application/x-mpegURL'
		if (source?.toLowerCase().endsWith('.mp3')) return 'audio/mpeg'
		return 'video/mp4'
	}, [type, src, source])

	const isAudioOnly = useMemo(() => {
		return source?.toLowerCase().endsWith('.mp3')
	}, [source])

	useEffect(() => {
		if (!videoRef.current || isLoading || !src) return

		const username = 'stenogram'
		const password = 'tl8hGB27pEnUoaNEak4VP6HMca3v'
		const encodedCredentials = btoa(`${username}:${password}`)

		if (isAudioOnly) {
			import('hls.js').then(({ default: Hls }) => {
				if (!videoRef.current) return
				if (!Hls.isSupported()) return

				const hls = new Hls({
					xhrSetup: xhr => {
						xhr.setRequestHeader('Authorization', `Basic ${encodedCredentials}`)
					}
				})

				hls.loadSource(src)
				hls.attachMedia(videoRef.current)

				return () => {
					hls.destroy()
				}
			})

			return
		}

		// @ts-ignore
		if (videojs.Vhs) {
			// @ts-ignore
			videojs.Vhs.xhr.beforeRequest = function (options) {
				options.headers = {
					...options.headers,
					Authorization: `Basic ${encodedCredentials}`
				}
				return options
			}
		}

		const player = videojs(videoRef.current, {
			controls: true,
			autoplay: true,
			liveui: true,
			inactivityTimeout: 0,
			liveTracker: { trackingThreshold: -1 },
			html5: {
				vhs: isSafari ? {} : { overrideNative: true }
			}
		})

		player.src({ src, type: correctType })

		// The timer for the right side has been changed from negative to positive
		// It's impossible to do it through the video player settings

		player.ready(() => {
			const remainingTimerEl = player.el().querySelector('.vjs-remaining-time')

			if (remainingTimerEl) {
				remainingTimerEl.textContent = ''
				player.on('timeupdate', () => {
					const ct = player.currentTime()
					const formatted = videojs.formatTime(ct, player.duration())

					if (!remainingTimerEl.firstChild) {
						const span = document.createElement('span')
						remainingTimerEl.appendChild(span)
					}

					remainingTimerEl.firstChild.textContent = formatted
				})
			}
		})

		playerRef.current = player

		if (onPlayerReady) {
			onPlayerReady(player)
		}

		return () => {
			playerRef.current?.dispose()
		}
	}, [isLoading, src, correctType, isSafari, isAudioOnly])

	return isLoading ? (
		<div className='w-full h-full bg-white' />
	) : isAudioOnly ? (
		<div className='flex h-[500px] w-full items-center'>
			<video
				className={cn('w-full h-[48px]', ready ? 'opacity-1' : 'opacity-0')}
				ref={videoRef}
				controls
				autoPlay
			/>
		</div>
	) : (
		<video
			className='w-full aspect-[459/255] object-cover video-js vjs-fluid'
			ref={videoRef}
		/>
	)
}

export default VideoPlayer
